import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone, Send, CheckCircle2, Loader2, Shield, Clock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const preferredLanguages = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish / Español' },
  { value: 'either', label: 'Either / Cualquiera' },
];

export const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', zipCode: '', coverageType: '', contactMethod: '', preferredLanguage: '', message: '', otherDetails: '', smsConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const coverageTypes = [
    { value: 'General Inquiry', label: t('contact.subjects.general') },
    { value: 'ACA Marketplace Insurance', label: t('contact.subjects.aca') },
    { value: 'Medicare', label: t('contact.subjects.medicare') },
    { value: 'Employer Health Benefits', label: t('contact.subjects.employer') },
    { value: 'Small Business Health Insurance', label: t('contact.subjects.smallBusiness') },
    { value: 'Individual & Family Health Insurance', label: t('contact.subjects.individualFamily') },
    { value: 'Premium Payment Assistance', label: t('contact.subjects.premiumAssistance') },
    { value: 'Dental & Vision Insurance', label: t('contact.subjects.dentalVision') },
    { value: 'Existing Client Support', label: t('contact.subjects.existingClient') },
    { value: 'Other', label: t('contact.subjects.other') },
  ];

  const contactMethods = [
    { value: 'email', label: t('contact.contactMethods.email') },
    { value: 'phone', label: t('contact.contactMethods.phone') },
    { value: 'either', label: t('contact.contactMethods.either') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.zipCode || !formData.coverageType || !formData.contactMethod) {
      toast.error(t('contact.errRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          zip_code: formData.zipCode,
          coverage_type: formData.coverageType,
          contact_method: formData.contactMethod,
          email: formData.email || null,
          phone: formData.phone || null,
          preferred_language: formData.preferredLanguage || null,
          message: [formData.message, formData.coverageType === 'Other' ? formData.otherDetails : ''].filter(Boolean).join(' — ') || null,
          sms_consent: formData.smsConsent,
        }),
      });
      if (!response.ok) throw new Error('Failed to submit contact form');
      setIsSubmitted(true);
      toast.success(t('contact.successToast'));
    } catch (error) {
      toast.error(t('contact.errorToast'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center py-20" data-testid="contact-success">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto text-center bg-white rounded-2xl p-12 shadow-xl">
            <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('contact.thankYou')}</h2>
            <p className="text-slate-600 mb-8">{t('contact.thankYouMsg')}</p>
            <Button onClick={() => setIsSubmitted(false)} className="btn-primary" data-testid="submit-another-btn">
              {t('contact.sendAnother')}
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden" data-testid="contact-page">
      <section className="gradient-hero py-20 lg:py-32" data-testid="contact-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial="initial" animate="animate" variants={fadeInUp} className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-slate-100">
              <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('contact.pageTitle')}</h1>
                <p className="text-slate-600">{t('contact.pageDesc')}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium">{t('contact.fullName')} <span className="text-red-500">*</span></Label>
                  <Input id="name" type="text" placeholder={t('contact.namePlaceholder')} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500" data-testid="input-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode" className="text-slate-700 font-medium">{t('contact.zipCode')} <span className="text-red-500">*</span></Label>
                  <Input id="zipCode" type="text" placeholder={t('contact.zipPlaceholder')} value={formData.zipCode} onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })} className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500" maxLength={5} data-testid="input-zipcode" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">{t('contact.subject')} <span className="text-red-500">*</span></Label>
                  <Select value={formData.coverageType} onValueChange={(value) => setFormData({ ...formData, coverageType: value })}>
                    <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-coverage"><SelectValue placeholder={t('contact.selectSubject')} /></SelectTrigger>
                    <SelectContent>
                      {coverageTypes.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.coverageType === 'Other' && (
                  <div className="space-y-2 md:col-span-2" data-testid="other-details-block">
                    <Label htmlFor="otherDetails" className="text-slate-700 font-medium">{t('contact.otherHelpLabel')}</Label>
                    <Textarea id="otherDetails" value={formData.otherDetails} onChange={(e) => setFormData({ ...formData, otherDetails: e.target.value })} className="min-h-[100px] rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 resize-none" data-testid="input-other-details" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">{t('contact.preferredContact')} <span className="text-red-500">*</span></Label>
                  <Select value={formData.contactMethod} onValueChange={(value) => setFormData({ ...formData, contactMethod: value })}>
                    <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-contact"><SelectValue placeholder={t('contact.selectContact')} /></SelectTrigger>
                    <SelectContent>
                      {contactMethods.map((method) => (<SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">{t('contact.preferredLanguage')}</Label>
                  <Select value={formData.preferredLanguage} onValueChange={(value) => setFormData({ ...formData, preferredLanguage: value })}>
                    <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-language"><SelectValue placeholder={t('contact.selectLanguage')} /></SelectTrigger>
                    <SelectContent>
                      {preferredLanguages.map((lang) => (<SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">{t('contact.email')}</Label>
                  <Input id="email" type="email" placeholder={t('contact.emailPlaceholder')} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500" data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium">{t('contact.phone')}</Label>
                  <Input id="phone" type="tel" placeholder={t('contact.phonePlaceholder')} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500" data-testid="input-phone" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700 font-medium">{t('contact.messageLabel')}</Label>
                  <Textarea id="message" placeholder={t('contact.messagePlaceholder')} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="min-h-[120px] rounded-xl border-slate-200 focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 resize-none" data-testid="input-message" />
                  <p className="text-xs text-slate-500 italic">{t('contact.messageNote')}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="sms-consent-block">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="smsConsent"
                      checked={formData.smsConsent}
                      onCheckedChange={(v) => setFormData({ ...formData, smsConsent: Boolean(v) })}
                      className="mt-1"
                      data-testid="sms-consent-checkbox"
                    />
                    <Label htmlFor="smsConsent" className="text-xs leading-relaxed text-slate-600 font-normal cursor-pointer">
                      I agree to receive SMS messages from Calyvra Insurance Agency regarding my insurance inquiries, applications, appointments, policy updates, and customer support. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out at any time. Reply HELP for assistance. Consent is not a condition of purchase.
                    </Label>
                  </div>
                  <p className="text-xs text-slate-500 pl-7">
                    By submitting this form, you agree to our{' '}
                    <Link to="/privacy-policy" className="text-sky-600 hover:underline font-medium" data-testid="contact-privacy-link">Privacy Policy</Link>{' '}
                    and{' '}
                    <Link to="/sms-terms" className="text-sky-600 hover:underline font-medium" data-testid="contact-sms-terms-link">SMS Terms &amp; Conditions</Link>.
                  </p>
                </div>
                <Button type="submit" disabled={isSubmitting} className="btn-accent w-full" data-testid="submit-contact-btn">
                  {isSubmitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('contact.sending')}</>) : (<>{t('contact.sendMessage')}<Send className="ml-2 h-4 w-4" /></>)}
                </Button>
                <p className="text-center text-sm text-slate-500">{t('contact.noObligation')}</p>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('contact.whyContact')}</h2>
                <p className="text-slate-600 mb-6">{t('contact.whyContactDesc')}</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white/50 backdrop-blur-sm p-4 rounded-xl">
                  <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0"><Clock className="h-5 w-5 text-sky-600" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{t('contact.quickResponse')}</h4>
                    <p className="text-slate-600 text-sm">{t('contact.quickResponseDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/50 backdrop-blur-sm p-4 rounded-xl">
                  <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0"><Shield className="h-5 w-5 text-sky-600" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{t('contact.licensedProfessionals')}</h4>
                    <p className="text-slate-600 text-sm">{t('contact.licensedProfessionalsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/50 backdrop-blur-sm p-4 rounded-xl">
                  <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0"><Heart className="h-5 w-5 text-sky-600" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900">{t('contact.noPressure')}</h4>
                    <p className="text-slate-600 text-sm">{t('contact.noPressureDesc')}</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-semibold mb-4">{t('contact.contactDirectly')}</h3>
                <div className="space-y-3">
                  <a href="tel:+13054323557" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors" data-testid="contact-phone-link">
                    <Phone className="h-5 w-5 text-sky-400" />
                    (305) 432-3557
                  </a>
                  <a href="mailto:info@calyvrainsurance.com" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors" data-testid="contact-email-link">
                    <Mail className="h-5 w-5 text-sky-400" />
                    info@calyvrainsurance.com
                  </a>
                  <div className="flex items-center gap-3 text-slate-300">
                    <MapPin className="h-5 w-5 text-sky-400" />
                    {t('contact.directLicensed')}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
